/**
 * useItemModal Composable
 * Handles item modal business logic (modifiers, pricing, validation)
 * Single Responsibility: Item modal logic
 * Compatible with Vue 2
 */

import flattenDepth from "lodash/flattenDepth";

/**
 * Composable for managing item modal operations
 * @param {Object} componentState - Component's reactive state object (from data())
 * @returns {Object} Item modal methods
 */
export function useItemModal(componentState) {
  if (!componentState) {
    console.warn("useItemModal: Component state is required");
    return {
      parseProduct: () => {},
      getSelectedModifiers: () => [],
      isValid: false,
      getTotalPrice: () => 0,
      prepareModifiers: () => [],
      getSelectedCountForGroup: () => 0,
      handleModifierSelected: () => {}
    };
  }

  /**
   * Parses product and initializes modifier groups
   */
  function parseProduct(item) {
    if (!item) return;

    componentState.singleSelections = {};
    componentState.modifierGroups = (item.modifier_groups || []).map(
      (group) => {
        const modifiers =
          group.modifiers?.map((mod) => ({
            ...mod,
            group: group.id,
            groupName: group.name
          })) || [];

        const isNewItem = !item.modifiers || item.modifiers.length === 0;
        const selected_modifiers = modifiers.filter((groupMod) => {
          if (item?.modifiers?.find(
            (m) => m.oryginal === groupMod.id && m.group === group.id
          )) return true;
          return isNewItem && groupMod.is_default;
        });

        if (group.max_selection === 1 && selected_modifiers.length > 0) {
          // Use Vue.set for Vue 2 reactivity if available
          if (componentState.$set) {
            componentState.$set(
              componentState.singleSelections,
              group.id,
              selected_modifiers[0]
            );
          } else {
            componentState.singleSelections[group.id] = selected_modifiers[0];
          }
        }

        return {
          ...group,
          modifiers,
          selected_modifiers: selected_modifiers || []
        };
      }
    );

    // Sort modifiers within each group by sort property and then by name
    componentState.modifierGroups.forEach((group) => {
      // First sort by sort value (fall back to sorting for backwards compatibility)
      group.modifiers.sort((a, b) => {
        const aSort = a.sort ?? a.sorting ?? 0;
        const bSort = b.sort ?? b.sorting ?? 0;
        if (aSort !== bSort) {
          return aSort - bSort;
        }
        // If sort values are the same, sort by name for stable order
        return a.name.localeCompare(b.name);
      });
    });

    // Sort modifier groups by sort property and then by name for stability
    componentState.modifierGroups.sort((a, b) => {
      const aSort = a.sort ?? a.sorting ?? 0;
      const bSort = b.sort ?? b.sorting ?? 0;
      if (aSort !== bSort) {
        return aSort - bSort;
      }
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Gets selected modifiers from all groups
   */
  function getSelectedModifiers() {
    const modifiers = componentState.modifierGroups
      .map((group) => group.selected_modifiers || [])
      .filter(Boolean);
    return flattenDepth(modifiers, 1);
  }

  /**
   * Validates if all required modifiers are selected
   */
  function isValid() {
    return componentState.modifierGroups.every((group) => {
      if (!group.min_selection || group.min_selection === 0) {
        return true;
      }
      const selectedCount = getSelectedCountForGroup(group.id);
      return selectedCount >= group.min_selection;
    });
  }

  /**
   * Gets selected count for a specific modifier group
   */
  function getSelectedCountForGroup(groupId) {
    return getSelectedModifiers().filter((mod) => mod.group === groupId).length;
  }

  /**
   * Handles modifier selection
   * Uses Vue.set for Vue 2 reactivity when modifying nested object properties
   */
  function handleModifierSelected({ group, modifier }) {
    // Find the group in modifierGroups array to ensure we're modifying the reactive object
    const groupIndex = componentState.modifierGroups.findIndex(
      (g) => g.id === group.id
    );
    if (groupIndex === -1) {
      console.warn("useItemModal: Group not found in modifierGroups", group.id);
      return;
    }

    const reactiveGroup = componentState.modifierGroups[groupIndex];

    if (reactiveGroup.max_selection === 1) {
      const currentSelectedModifiers = reactiveGroup.selected_modifiers || [];
      const isCurrentlySelected = currentSelectedModifiers.some(
        (m) => m.id === modifier.id
      );

      if (isCurrentlySelected) {
        // Deselect: remove selection (user clicked the same option again)
        componentState.$set(
          componentState.singleSelections,
          reactiveGroup.id,
          null
        );
        componentState.$set(reactiveGroup, "selected_modifiers", []);
        // Also update the array item to force reactivity
        componentState.$set(componentState.modifierGroups, groupIndex, {
          ...reactiveGroup,
          selected_modifiers: []
        });
      } else {
        // Select new modifier
        componentState.$set(
          componentState.singleSelections,
          reactiveGroup.id,
          modifier
        );
        // Create a new array to ensure reactivity
        const newSelectedModifiers = [modifier];
        componentState.$set(
          reactiveGroup,
          "selected_modifiers",
          newSelectedModifiers
        );
        // Also update the array item to force reactivity
        componentState.$set(componentState.modifierGroups, groupIndex, {
          ...reactiveGroup,
          selected_modifiers: newSelectedModifiers
        });
      }
    } else {
      // Checkbox behavior (multiple selection)
      const currentSelections = reactiveGroup.selected_modifiers || [];
      const isSelected = currentSelections.find((m) => m.id === modifier.id);

      if (isSelected) {
        // Remove modifier
        const newSelections = currentSelections.filter(
          (m) => m.id !== modifier.id
        );
        componentState.$set(reactiveGroup, "selected_modifiers", newSelections);
      } else {
        // Add modifier
        if (
          !reactiveGroup.max_selection ||
          currentSelections.length < reactiveGroup.max_selection
        ) {
          const newSelections = [...currentSelections, modifier];
          componentState.$set(
            reactiveGroup,
            "selected_modifiers",
            newSelections
          );
        }
      }
    }
  }

  /**
   * Prepares modifiers for cart submission
   */
  function prepareModifiers(modifiers) {
    return modifiers.map((m) => ({
      cost: m.cost,
      group: m.group,
      name: m.name,
      groupName: m.groupName,
      oryginal: m.id,
      price: m.price
    }));
  }

  /**
   * Calculates total price for item with modifiers
   */
  function getTotalPrice(item, quantity) {
    if (!item) return 0;

    const selectedModifiers = getSelectedModifiers();
    const modifiersTotal = selectedModifiers
      .map((modifier) => modifier.price)
      .reduce((total, modifierPrice) => (total += modifierPrice), 0);

    const totalPricePerOne = (modifiersTotal ?? 0) + item.price;
    return totalPricePerOne * quantity;
  }

  return {
    parseProduct,
    getSelectedModifiers,
    isValid,
    getTotalPrice,
    prepareModifiers,
    getSelectedCountForGroup,
    handleModifierSelected
  };
}
